import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, DownloadIcon, Loader2Icon, XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { PdfCanvasViewer } from './PdfCanvasViewer';
const ease = [0.23, 1, 0.32, 1] as const;
interface Props {
  /** Bruges som tilgængeligt navn på siderne, fx "Tilbud". */
  title: string;
  fileName: string;
  /** Danner PDF'en ud fra de senest gemte data. */
  build: () => Promise<ArrayBuffer>;
  onClose: () => void;
}

/** Fælles forhåndsvisning for tilbud og fakturaer — samme fil som download. */
export function PdfPreviewDialog({
  title,
  fileName,
  build,
  onClose
}: Props) {
  const [data, setData] = useState<ArrayBuffer | null>(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    let active = true;
    setError('');
    build().then(bytes => {
      if (active) setData(bytes);
    }).catch((err: unknown) => {
      console.error('[PDF] kunne ikke dannes', err);
      if (active) setError('PDF’en kunne ikke dannes. Prøv igen.');
    });
    return () => {
      active = false;
    };
    // build ændres når de gemte data ændres.
  }, [build]);
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  function download() {
    if (!data || downloading) return;
    setDownloading(true);
    try {
      const url = URL.createObjectURL(new Blob([data], {
        type: 'application/pdf'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } finally {
      setDownloading(false);
    }
  }
  return <div className="fixed inset-0 z-50 flex flex-col bg-black/60 px-3 py-4 sm:px-4 sm:py-6" role="dialog" aria-modal="true" aria-labelledby="pdf-dialog-titel" onClick={event => {
    if (event.target === event.currentTarget) onClose();
  }} data-id="element-374" data-mp="37a1pp9gafb3f">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.24,
      ease
    }} className="mx-auto flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-card bg-white" data-id="element-375" data-mp="1g4jjkke5brpm">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4" data-id="element-376" data-mp="jh6a016tm0dn">
          <div className="min-w-0" data-id="element-377" data-mp="x8l2qzqj5fxw">
            <h2 id="pdf-dialog-titel" className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-378" data-mp="x9w30pkro5k9">
              Forhåndsvisning
            </h2>
            <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-379" data-mp="4qffumhrap7d">{fileName}</p>
          </div>
          <div className="flex items-center gap-2" data-id="element-380" data-mp="qoq8m3yuf4f">
            <Button variant="secondary" size="sm" onClick={download} disabled={!data || downloading} data-id="element-381" data-mp="o2lsvcdgfqf">
              <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-382" data-mp="3mpi0vu47thjt" />
              Download PDF
            </Button>
            <button type="button" onClick={onClose} aria-label="Luk forhåndsvisning" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-383" data-mp="3ctezwrl3noyg">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-384" data-mp="ea85f4utzal0" />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1" data-id="element-385" data-mp="3vj4xtnk5squl">
          {error ? <p role="alert" className="flex items-center justify-center gap-2 p-10 text-sm text-danger" data-id="element-386" data-mp="4ck764y0nky3">
              <AlertCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-387" data-mp="2k3g66ivyn3ng" />
              {error}
            </p> : data ? <PdfCanvasViewer data={data} title={title} data-id="element-388" data-mp="2p4vuqkxcjsal" /> : <p className="flex items-center justify-center gap-2 bg-canvas p-10 text-sm text-ink-muted" data-id="element-389" data-mp="35bs6aeup6xj6">
              <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-390" data-mp="30j8mh05f365x" />
              Danner PDF …
            </p>}
        </div>
      </motion.div>
    </div>;
}